import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Users,
  DollarSign,
  Building2,
  FileSignature,
  Search,
  X,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        <div className="p-1.5 rounded-sm bg-secondary">
          <Icon size={14} strokeWidth={2.2} />
        </div>
      </div>
      <div className="kpi-num text-3xl mt-3">{value}</div>
    </div>
  );
}

function tierBadge(tier) {
  const t = tier || "free";
  const map = {
    free: "bg-muted text-muted-foreground",
    starter: "bg-blue-100 text-blue-800",
    professional: "bg-purple-100 text-purple-800",
    team: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span
            className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full font-semibold ${map[t] || map.free}`}
    >
      {t}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.is_admin) {
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  const loadStats = async () => {
    try {
      const r = await api.get("/admin/stats");
      setStats(r.data);
    } catch (_) {
      setStats(null);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (tierFilter) params.tier = tierFilter;
      const r = await api.get("/admin/users", { params });
      setUsers(r.data.users || []);
    } catch (_) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      loadStats();
      loadUsers();
    }
  }, [user]);

  useEffect(() => {
    if (!user?.is_admin) return;
    const t = setTimeout(loadUsers, 300);
    return () => clearTimeout(t);
  }, [search, tierFilter]);

  const openDetail = async (uid) => {
    setDetailLoading(true);
    setDetail({ id: uid });
    try {
      const r = await api.get(`/admin/users/${uid}`);
      setDetail(r.data);
    } catch (_) {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="p-10 text-muted-foreground" data-testid="admin-loading">
        Loading…
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="p-10" data-testid="admin-forbidden">
        <h1 className="text-2xl font-semibold">Access denied</h1>
        <p className="text-muted-foreground mt-2">
          You do not have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="admin-dashboard">
      <header className="flex items-center gap-3">
        <div className="w-11 h-11 grid place-items-center rounded-full bg-[var(--app-black)] text-white">
          <ShieldCheck size={20} strokeWidth={2.2} />
        </div>
        <div>
          <span className="app-eyebrow">/ Platform</span>
          <h1 className="text-3xl font-display leading-none mt-1.5">
            Admin Console
          </h1>
          <p className="text-xs text-[var(--app-text-muted)] mt-1">
            Customer accounts &amp; platform usage
          </p>
        </div>
      </header>

      {stats && (
        <section
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          data-testid="admin-stats-grid"
        >
          <StatCard
            icon={Users}
            label="Total Users"
            value={stats.total_users}
            testid="stat-total-users"
          />
          <StatCard
            icon={DollarSign}
            label="Paying Users"
            value={stats.paying_users}
            testid="stat-paying-users"
          />
          <StatCard
            icon={Building2}
            label="Properties"
            value={stats.total_properties}
            testid="stat-total-properties"
          />
          <StatCard
            icon={FileSignature}
            label="Offers"
            value={stats.total_offers}
            testid="stat-total-offers"
          />
        </section>
      )}

      {stats && (
        <section className="card-flat p-5" data-testid="admin-revenue-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Lifetime Revenue
              </div>
              <div className="kpi-num text-4xl mt-2">
                {money(stats.total_revenue)}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-center">
              {["free", "starter", "professional", "team"].map((t) => (
                <div key={t} data-testid={`tier-count-${t}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {t}
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {stats.tier_breakdown?.[t] ?? 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="card-flat p-5" data-testid="admin-users-panel">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1">
            <Search size={16} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by email…"
              className="flex-1 bg-transparent border-b border-border py-1 focus:outline-none focus:border-foreground"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-users-search"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="border border-[var(--app-line)] rounded-full px-4 py-1.5 text-sm bg-white"
            data-testid="admin-users-tier-filter"
          >
            <option value="">All tiers</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="team">Team</option>
          </select>
        </div>

        {loading ? (
          <div
            className="text-sm text-muted-foreground py-6"
            data-testid="admin-users-loading"
          >
            Loading users…
          </div>
        ) : users.length === 0 ? (
          <div
            className="text-sm text-muted-foreground py-6"
            data-testid="admin-users-empty"
          >
            No users match those filters.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="admin-users-table-wrap">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Signed Up</th>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Properties</th>
                  <th className="py-2 pr-4">Offers</th>
                  <th className="py-2 pr-4">Leads</th>
                  <th className="py-2 pr-4">DocuSign</th>
                  <th className="py-2 pr-0"></th>
                </tr>
              </thead>
              <tbody data-testid="admin-users-tbody">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-border/60 hover:bg-secondary/30"
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    <td className="py-3 pr-4">
                      <div className="font-medium">{u.email}</div>
                      {u.name && (
                        <div className="text-xs text-muted-foreground">
                          {u.name}
                        </div>
                      )}
                      {u.is_admin && (
                        <span className="inline-block mt-1 text-[9px] uppercase tracking-widest bg-foreground text-background px-1.5 py-0.5 rounded-sm">
                          Admin
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      {fmtDate(u.created_at)}
                    </td>
                    <td className="py-3 pr-4">{tierBadge(u.subscription_tier)}</td>
                    <td className="py-3 pr-4">{u.properties_count}</td>
                    <td className="py-3 pr-4">{u.offers_count}</td>
                    <td className="py-3 pr-4">{u.leads_count}</td>
                    <td className="py-3 pr-4">
                      {u.docusign_connected ? (
                        <span className="text-emerald-700 text-xs">Connected</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-0">
                      <button
                        onClick={() => openDetail(u.id)}
                        className="text-xs underline hover:no-underline"
                        data-testid={`admin-user-view-${u.id}`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {detail && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setDetail(null)}
          data-testid="admin-user-detail-overlay"
        >
          <div
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-user-detail-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold" data-testid="detail-email">
                  {detail.email || "…"}
                </h2>
                <div className="text-xs text-muted-foreground mt-1">
                  {detail.name || ""} · Joined {fmtDate(detail.created_at)}
                </div>
              </div>
              <button
                onClick={() => setDetail(null)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="admin-user-detail-close"
              >
                <X size={20} />
              </button>
            </div>

            {detailLoading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Subscription
                    </div>
                    <div className="mt-1">
                      {tierBadge(detail.subscription_tier)}
                      {detail.subscription_expires_at && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Renews {fmtDate(detail.subscription_expires_at)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      DocuSign
                    </div>
                    <div className="mt-1 text-sm">
                      {detail.docusign?.connected ? (
                        <>
                          <span className="text-emerald-700">Connected</span>
                          <div className="text-xs text-muted-foreground">
                            {detail.docusign.account_email}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">Not connected</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    Usage
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[
                      ["Properties", detail.usage?.properties],
                      ["Offers", detail.usage?.offers],
                      ["Leads", detail.usage?.leads],
                      ["Buyers", detail.usage?.buyers],
                      ["Campaigns", detail.usage?.campaigns],
                    ].map(([k, v]) => (
                      <div key={k} className="card-flat py-3">
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                          {k}
                        </div>
                        <div className="text-lg font-semibold mt-1">{v ?? 0}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    Recent Transactions
                  </div>
                  {(!detail.transactions || detail.transactions.length === 0) ? (
                    <div className="text-sm text-muted-foreground">
                      No transactions yet.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-widest text-muted-foreground text-left">
                        <tr>
                          <th className="py-1">Date</th>
                          <th className="py-1">Plan</th>
                          <th className="py-1">Amount</th>
                          <th className="py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.transactions.map((t) => (
                          <tr key={t.session_id} className="border-t border-border/60">
                            <td className="py-2">{fmtDate(t.created_at)}</td>
                            <td className="py-2">{t.plan_id}</td>
                            <td className="py-2">{money(t.amount)}</td>
                            <td className="py-2">
                              <span
                                className={
                                  t.payment_status === "paid"
                                    ? "text-emerald-700"
                                    : "text-muted-foreground"
                                }
                              >
                                {t.payment_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
